import { useState, useRef } from 'react'
import { useApolloClient } from '@apollo/client'
import { Input } from '@/components/Input'
import { Button } from '@/components/ui/Button'
import { IconUpload } from '@/components/icons'
import { GET_BRAND_INFO } from '@/graphql/queries/team'
import { uploadFileToS3, generateUploadPath } from '@/lib/upload'

const S3_BUCKET = 'rollavideo-retool'

// Brandfetch's raw v2 response shape (rolla-db's getBrandInfo passes this through untouched as
// `obj`) — only the fields we actually read.
interface BrandfetchApiResponse {
  name?: string | null
  logos?: {
    type?: string
    formats?: { src?: string; width?: number | null; height?: number | null }[]
  }[]
  images?: {
    type?: string
    formats?: { src?: string }[]
  }[]
}

// Brandfetch's "logo" type is often a wide wordmark, not the square mark this app actually wants
// (see the "Square, 512×512px minimum" caption on the Branding page) — "icon" is the square
// app-icon/favicon-style asset, so prefer that when available. Within whichever logo entry gets
// picked, also prefer a near-square format over the first one listed, since a single logo entry
// can itself carry multiple format variants (e.g. a wordmark alongside a mark-only crop).
function pickBrandfetchLogo(obj: BrandfetchApiResponse): string | null {
  const logo = obj.logos?.find((l) => l.type === 'icon') ?? obj.logos?.find((l) => l.type === 'logo') ?? obj.logos?.[0]
  const formats = logo?.formats ?? []
  const square = formats.find((f) => f.width && f.height && Math.abs(f.width / f.height - 1) < 0.15)
  return square?.src ?? formats[0]?.src ?? null
}

function pickBrandfetchHeroImage(obj: BrandfetchApiResponse): string | null {
  const banner = obj.images?.find((i) => i.type === 'banner')
  return banner?.formats?.[0]?.src ?? null
}

// Strips scheme, "www.", and any path/query/fragment/trailing slash, so a pasted full URL
// (e.g. "https://www.reflex.careers/about") normalizes to a bare domain ("reflex.careers")
// both in the field itself and in what's sent to the lookup API.
function normalizeDomain(input: string): string {
  return input
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split(/[/?#]/)[0]
}

export interface CompanyBrandingValue {
  companyURL: string
  companyName: string
  logoURL: string | null
  heroImageURL: string | null
}

interface CompanyBrandingFormProps {
  value: CompanyBrandingValue
  onChange: (value: CompanyBrandingValue) => void
  // Used to namespace the logo's S3 upload path — pass the team id once it exists (Signup already
  // has one by the time this step renders); callers without one yet can pass a temp namespace.
  uploadNamespace: string
  onError: (message: string) => void
  // Hides the name/logo fields until a lookup has been attempted (success or "no match") — forces
  // public self-serve signups through the lookup first, since it's the only way to get a logo
  // without a separate manual-upload step. AddAccountModal (internal admin, standing up a demo
  // account) opts out — they're trusted to skip straight to typing a name if they want to.
  requireLookupFirst?: boolean
  // Lets a parent (e.g. SignupPage, to gate its own submit button) know once a lookup attempt has
  // completed, mirroring the internal brandfetchChecked state that drives the field gate here.
  onLookupCheckedChange?: (checked: boolean) => void
}

// Shared by SignupPage's "Company Details" step and AddAccountModal — both let an operator look
// up a company by domain (via Brandfetch), confirm/edit the name, and swap the logo.
export function CompanyBrandingForm({
  value,
  onChange,
  uploadNamespace,
  onError,
  requireLookupFirst = false,
  onLookupCheckedChange,
}: CompanyBrandingFormProps) {
  const apolloClient = useApolloClient()
  const [checkingBrandfetch, setCheckingBrandfetch] = useState(false)
  const [brandfetchChecked, setBrandfetchChecked] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)

  function markChecked() {
    setBrandfetchChecked(true)
    onLookupCheckedChange?.(true)
  }

  async function handleLogoUpload(file: File) {
    setUploadingLogo(true)
    try {
      const path = generateUploadPath('EnterpriseLogos', uploadNamespace, file.name)
      const url = await uploadFileToS3(file, S3_BUCKET, path)
      onChange({ ...value, logoURL: url })
    } catch {
      onError('Failed to upload logo. Please try again.')
    } finally {
      setUploadingLogo(false)
    }
  }

  async function handleBrandfetchLookup() {
    if (!value.companyURL.trim()) return
    setCheckingBrandfetch(true)
    onError('')
    try {
      const domain = normalizeDomain(value.companyURL)
      const { data } = await apolloClient.query({
        query: GET_BRAND_INFO,
        variables: { domain },
        fetchPolicy: 'network-only',
      })
      markChecked()
      const info = data?.getBrandInfo
      // returnCode 0 with no `name` is Brandfetch's own "no match for this domain" case, not an
      // error — the manual-name fallback below covers it either way.
      if (info?.returnCode === 0 && info.obj?.name) {
        const obj = info.obj as BrandfetchApiResponse
        onChange({
          ...value,
          companyName: obj.name ?? '',
          logoURL: pickBrandfetchLogo(obj),
          heroImageURL: pickBrandfetchHeroImage(obj),
        })
      }
    } catch {
      markChecked()
    } finally {
      setCheckingBrandfetch(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="companyURL" className="block text-sm font-medium text-gray-700">
          Company website
        </label>
        <div className="mt-1 flex gap-2">
          <Input
            id="companyURL"
            type="text"
            value={value.companyURL}
            onChange={(e) => onChange({ ...value, companyURL: e.target.value })}
            onBlur={(e) => {
              const normalized = normalizeDomain(e.target.value)
              if (normalized !== value.companyURL) onChange({ ...value, companyURL: normalized })
            }}
            placeholder="yourcompany.com"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={handleBrandfetchLookup}
            disabled={checkingBrandfetch || !value.companyURL.trim()}
            className="whitespace-nowrap"
          >
            {checkingBrandfetch ? 'Looking up...' : 'Look up'}
          </Button>
        </div>
        {brandfetchChecked && !value.companyName && (
          <p className="mt-2 text-xs text-gray-500">We couldn&apos;t find a match — enter your company name below.</p>
        )}
      </div>

      {(!requireLookupFirst || brandfetchChecked) && (
        <>
          {(value.logoURL || value.heroImageURL) && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-center">
              <p className="text-xs text-gray-500">We've pulled your branding. Edit or replace below.</p>
            </div>
          )}

          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleLogoUpload(file)
              e.target.value = ''
            }}
          />
          <div className="flex items-end gap-3">
            <button
              type="button"
              onClick={() => !uploadingLogo && logoInputRef.current?.click()}
              disabled={uploadingLogo}
              title={value.logoURL ? 'Swap logo' : 'Upload logo'}
              className="group relative h-10 w-10 flex-shrink-0 overflow-hidden rounded border border-gray-200 bg-gray-50"
            >
              {value.logoURL ? (
                <>
                  <img src={value.logoURL} alt="" className="h-full w-full object-contain" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-[9px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                    {uploadingLogo ? '...' : 'Swap'}
                  </div>
                </>
              ) : (
                <div className="flex h-full w-full items-center justify-center text-gray-400 group-hover:text-gray-600">
                  <IconUpload className="h-4 w-4" />
                </div>
              )}
            </button>
            <div className="flex-1">
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                Company name
              </label>
              <Input
                id="companyName"
                type="text"
                required
                value={value.companyName}
                onChange={(e) => onChange({ ...value, companyName: e.target.value })}
                placeholder="Acme Inc."
                className="mt-1"
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
